/**
 * The document frame: pdf.js, run inside a sandboxed iframe with no origin.
 *
 * A PDF is somebody's upload, and this app's origin holds the session, so the
 * reader never runs as the app: the frame is `srcdoc` with
 * `sandbox="allow-scripts"` and nothing else — an opaque origin that cannot
 * read the page's storage or call the API. The page hands it everything it
 * needs by postMessage (protocol.ts): pdf.js's own source, which the frame
 * loads from URLs it makes itself — the library from a blob: URL, the worker
 * from a data: URL, since a frame with no origin may import a blob: module
 * but may not start a Worker from one (Chrome refuses the script "from origin
 * null"), and loading the page's files itself would need CORS — the
 * document's bytes, and the colours of the page around it. The frame draws
 * pages as they scroll into view and reports the page, the zoom and the keys
 * it does not use; the controls are the page's (DocumentView.tsx).
 *
 * Plain script in a string: a frame with no origin imports nothing of ours.
 */
export const FRAME_HTML = String.raw`<!doctype html>
<html><head><meta charset="utf-8">
<style>
  html, body { margin: 0; height: 100%; background: var(--bg, #222); color: var(--fg, #ddd); font: 13px system-ui, sans-serif; overflow: hidden; }
  #scroll { position: absolute; inset: 0; overflow: auto; }
  #pages { display: flex; flex-direction: column; align-items: center; gap: 16px; padding: 16px; box-sizing: border-box; min-width: 100%; width: max-content; }
  .page { position: relative; flex: none; background: #fff; box-shadow: 0 1px 4px rgba(0, 0, 0, .45); }
  .page canvas { display: block; width: 100%; height: 100%; }
  .textLayer { position: absolute; inset: 0; overflow: hidden; line-height: 1; text-size-adjust: none; forced-color-adjust: none; transform-origin: 0 0; z-index: 2; }
  .textLayer :is(span, br) { color: transparent; position: absolute; white-space: pre; cursor: text; transform-origin: 0% 0%; }
  .textLayer ::selection { background: rgba(0, 110, 255, .3); }
  #status { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; opacity: .7; pointer-events: none; }
</style></head>
<body>
<div id="scroll"><div id="pages"></div></div>
<div id="status">Loading the document…</div>
<script>
(() => {
  const post = (m) => parent.postMessage(m, '*');
  const scroll = document.getElementById('scroll');
  const pagesEl = document.getElementById('pages');
  const status = document.getElementById('status');
  // A page's canvas kept to what a browser will draw: between a tenth and four
  // times its printed size (a PDF point is 1/72 inch, a CSS pixel 1/96, so
  // printed size draws 4/3 pixels a point).
  const PRINTED = 4 / 3, MIN_SCALE = PRINTED / 10, MAX_SCALE = PRINTED * 4;
  const PAD = 16, STEP = 1.25;
  let pdfjs = null, doc = null, pages = [], sizes = [];
  // The zoom runs from the whole page (all the way out) past the width to as
  // far in as a page's canvas allows (the user, 2026-10-09: the width is the
  // digit ladder's top, not the zoom's). 'ratio' is where it is, as a share of
  // the width fit — the percentage the page shows, 100 at the width; 'top' is
  // as far in as it goes, the same way; 'fit' names the whole page or the
  // width when the zoom is there, else null; 'scale' is CSS pixels a PDF point.
  let ratio = 1, top = 1, fit = 'width', scale = 1, current = 1, announced = false;
  // The view's size when the pages were last laid out: a resize keeps the
  // point that was in the middle of that view, not of the new one.
  let viewWidth = 0, viewHeight = 0;
  // One frame shows document after document: each open ends the one before
  // (its loading, its pages, its worker). The page keeps the frame across a
  // switch, since a frame put in place of another was drawn and not shown.
  // What the frame says of a document carries the page's token for that open,
  // so a word about the one before, said before the next open arrived, is not
  // taken for the next.
  let generation = 0, token = 0, task = null, worker = null, workerUrl = null;

  const say = (text) => { status.textContent = text; status.style.display = text ? 'flex' : 'none'; };
  const fail = (message) => { say(''); post({ type: 'error', message, token }); };
  // What went on inside, for the page's console: a frame with no origin has none anyone reads.
  const note = (message) => post({ type: 'log', message: String(message) });
  const theme = (m) => {
    if (m.bg) document.documentElement.style.setProperty('--bg', m.bg);
    if (m.fg) document.documentElement.style.setProperty('--fg', m.fg);
  };

  // The two scales the frame's size gives page 1: filling the width, and seen
  // whole — never larger than the width, so the whole page is the zoom's
  // floor (and the width itself, for a page wider in shape than the frame).
  function fits() {
    const [w, h] = sizes[0] || [612, 792];
    const width = Math.max(MIN_SCALE, Math.min(MAX_SCALE, Math.max(1, scroll.clientWidth - 2 * PAD) / w));
    const page = Math.min(width, Math.max(MIN_SCALE, Math.max(1, scroll.clientHeight - 2 * PAD) / h));
    return { width, page };
  }

  // Pages drawn as they come into view, and let go as they leave: a long
  // document costs the screen's worth of canvases, not the whole document's.
  const observer = new IntersectionObserver((entries) => {
    for (const e of entries) {
      const p = pages[Number(e.target.dataset.page) - 1];
      if (!p) continue;
      if (e.isIntersecting) draw(p); else clear(p);
    }
  }, { root: scroll, rootMargin: '600px 0px' });

  function layout() {
    observer.disconnect();
    pagesEl.replaceChildren();
    pages = sizes.map(([w, h], i) => {
      const el = document.createElement('div');
      el.className = 'page';
      el.dataset.page = String(i + 1);
      el.style.width = Math.floor(w * scale) + 'px';
      el.style.height = Math.floor(h * scale) + 'px';
      el.style.setProperty('--scale-factor', String(scale));
      pagesEl.appendChild(el);
      return { el, drawn: 0, drawing: 0, task: null };
    });
    for (const p of pages) observer.observe(p.el);
    post({ type: 'zoom', fit, percent: Math.round(ratio * 100), max: Math.round(top * 100), token });
  }

  async function draw(p) {
    if (p.drawn === scale || p.drawing === scale) return;
    p.drawing = scale;
    const n = Number(p.el.dataset.page);
    const shown = doc;
    try {
      const page = await shown.getPage(n);
      if (p.drawing !== scale || shown !== doc) return;
      const dpr = window.devicePixelRatio || 1;
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      const task = page.render({ canvasContext: canvas.getContext('2d'), viewport, transform: dpr === 1 ? undefined : [dpr, 0, 0, dpr, 0, 0] });
      p.task = task;
      await task.promise;
      p.task = null;
      if (p.drawing !== scale) return;
      const text = document.createElement('div');
      text.className = 'textLayer';
      p.el.replaceChildren(canvas, text);
      p.drawn = scale;
      // Selectable text over the picture; a page with none draws nothing here.
      new pdfjs.TextLayer({ textContentSource: page.streamTextContent(), container: text, viewport }).render().catch(() => {});
      if (!announced && shown === doc) {
        announced = true;
        post({ type: 'ready', token });
      }
    } catch (err) {
      if (!(err && err.name === 'RenderingCancelledException')) note('page ' + n + ' not drawn: ' + ((err && err.message) || err));
    } finally {
      if (p.drawing === scale) p.drawing = 0;
    }
  }

  function clear(p) {
    if (p.task) { try { p.task.cancel(); } catch (_) {} p.task = null; }
    p.el.replaceChildren();
    p.drawn = 0;
    p.drawing = 0;
  }

  function setCurrent(page) {
    if (page !== current) { current = page; post({ type: 'page', page: current, token }); }
  }

  // At the top of the document the current page is the first. Elsewhere the
  // current page (one asked for, or the last reported) stays while the view
  // shows all of it; failing that, it is the last page at the bottom of the
  // document, and anywhere else the page the view shows the most of, the
  // first of equals. Not the page nearest the middle of the view: with pages
  // shorter than half the view (a slide deck's, a landscape page's) that is
  // page 2 at the top of page 1, and a page jumped to is reported as another.
  function updateCurrent() {
    const viewTop = scroll.scrollTop, viewBottom = viewTop + scroll.clientHeight;
    const shown = (p) => Math.min(viewBottom, p.el.offsetTop + p.el.offsetHeight) - Math.max(viewTop, p.el.offsetTop);
    const kept = pages[current - 1];
    if (viewTop < 1) return setCurrent(1);
    if (kept && shown(kept) >= kept.el.offsetHeight) return;
    if (viewBottom >= scroll.scrollHeight - 1) return setCurrent(pages.length || 1);
    let best = 1, most = -Infinity;
    for (const p of pages) {
      const s = shown(p);
      if (s > most) { most = s; best = Number(p.el.dataset.page); }
    }
    setCurrent(best);
  }
  let scrollFrame = 0;
  scroll.addEventListener('scroll', () => {
    if (scrollFrame) return;
    scrollFrame = requestAnimationFrame(() => { scrollFrame = 0; updateCurrent(); });
  }, { passive: true });

  // A jump, never a glide: the page asked for is there at once (the user,
  // 2026-10-09), and is the current page, even where the document cannot
  // scroll it to the top of the view.
  function goto(page) {
    const p = pages[Math.min(pages.length, Math.max(1, page | 0)) - 1];
    if (!p) return;
    scroll.scrollTo({ top: Math.max(0, p.el.offsetTop - PAD), behavior: 'instant' });
    setCurrent(Number(p.el.dataset.page));
  }

  // 'm.mode': a step in or out, one of the fits ('page', 'width'), a share of
  // the way between the two ('between', the digit ladder: evenly on a log
  // scale, 0 the whole page, 1 the width), or 'keep' — the same share of the
  // width, in a frame of a new size. A step never passes the width without
  // stopping there. Keeps the place in the document: the point in the middle
  // of the view, as a share of the document's height and width, stays in the
  // middle — the middle of the view as it was laid out, since a resize has
  // already changed the view's size when it gets here. 'fromTop' starts the
  // view at the top of page 1 instead: a document just opened has no place to
  // keep (the middle of an empty view is half its height, which put it
  // halfway down the document), and a view at the top stays there.
  function zoom(m, fromTop) {
    if (!doc) return;
    const share = scroll.scrollHeight ? (scroll.scrollTop + (viewHeight || scroll.clientHeight) / 2) / scroll.scrollHeight : 0;
    const across = scroll.scrollWidth ? (scroll.scrollLeft + (viewWidth || scroll.clientWidth) / 2) / scroll.scrollWidth : 0.5;
    const { width, page } = fits();
    const floor = page / width;
    top = Math.max(1, MAX_SCALE / width);
    let r = ratio;
    if (m.mode === 'in') r = ratio * STEP;
    else if (m.mode === 'out') r = ratio / STEP;
    else if (m.mode === 'width') r = 1;
    else if (m.mode === 'page') r = floor;
    else if (m.mode === 'between') r = Math.pow(floor, 1 - Math.min(1, Math.max(0, Number(m.share) || 0)));
    if ((m.mode === 'in' || m.mode === 'out') && ((ratio < 1 && r > 1) || (ratio > 1 && r < 1))) r = 1;
    ratio = Math.min(top, Math.max(floor, r));
    fit = ratio <= floor ? 'page' : Math.abs(ratio - 1) < 1e-9 ? 'width' : null;
    scale = ratio * width;
    layout();
    scroll.scrollTop = fromTop ? 0 : share * scroll.scrollHeight - scroll.clientHeight / 2;
    scroll.scrollLeft = across * scroll.scrollWidth - scroll.clientWidth / 2;
    viewHeight = scroll.clientHeight;
    viewWidth = scroll.clientWidth;
    updateCurrent();
  }

  // The document follows the frame's size, once the size has settled: a fit
  // stays that fit, a zoom between stays its share of the width, a view at
  // the top of the document stays at the top (f, a panel closing). A window
  // being dragged would otherwise redraw every page every frame. A frame of
  // no size has no fits.
  let resizeTimer = 0;
  new ResizeObserver(() => {
    if (!doc || !scroll.clientWidth || !scroll.clientHeight) return;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => zoom({ mode: fit || 'keep' }, scroll.scrollTop < 1), 150);
  }).observe(scroll);

  // A script as a data: URL: base64 of its UTF-8 bytes, in chunks, since
  // String.fromCharCode cannot take a million arguments at once.
  function dataUrl(source) {
    const bytes = new TextEncoder().encode(source);
    let binary = '';
    for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
    return 'data:text/javascript;base64,' + btoa(binary);
  }

  async function open(m) {
    const mine = ++generation;
    const isCurrent = () => mine === generation;
    try {
      token = m.token;
      theme(m);
      // The document before this one goes first: nothing of it is drawn again.
      const before = task, beforeWorker = worker;
      task = null; worker = null; doc = null;
      observer.disconnect();
      pagesEl.replaceChildren();
      pages = []; sizes = [];
      announced = false; current = 1;
      scroll.scrollTop = 0;
      say('Loading the document…');
      if (before) await before.destroy().catch(() => {});
      if (beforeWorker) beforeWorker.terminate();
      // pdf.js once per frame; its worker's script once, as a data: URL.
      pdfjs ??= await import(URL.createObjectURL(new Blob([m.lib], { type: 'text/javascript' })));
      workerUrl ??= dataUrl(m.worker);
      if (!isCurrent()) return;
      const w = new Worker(workerUrl, { type: 'module' });
      // pdf.js waits for its worker without end; a worker that cannot start says so here.
      w.onerror = (ev) => { if (w === worker) fail('The document reader could not start' + (ev && ev.message ? ': ' + ev.message : '.')); };
      worker = w;
      pdfjs.GlobalWorkerOptions.workerPort = w;
      // No forms, no scripts: a document is drawn and read, never run (pdf.js 6
      // generates no code of its own, so there is no eval left to switch off).
      const t = pdfjs.getDocument({ data: new Uint8Array(m.pdf), enableXfa: false, useSystemFonts: true });
      task = t;
      const opened = await t.promise;
      const measured = [];
      for (let i = 1; i <= opened.numPages; i++) {
        const viewport = (await opened.getPage(i)).getViewport({ scale: 1 });
        measured.push([viewport.width, viewport.height]);
      }
      if (!isCurrent()) return;
      doc = opened;
      sizes = measured;
      say('');
      post({ type: 'loaded', pages: doc.numPages, token });
      zoom({ mode: 'width' }, true);
    } catch (err) {
      if (isCurrent()) fail(String((err && err.message) || err || 'The document could not be opened.'));
    }
  }

  window.addEventListener('message', (e) => {
    if (e.source !== parent) return;
    const m = e.data || {};
    if (m.type === 'open') open(m);
    else if (m.type === 'theme') theme(m);
    else if (m.type === 'goto') goto(m.page);
    else if (m.type === 'zoom') zoom(m);
  });

  // The keys the reader uses stay here; every other key is the page's — its
  // shortcuts, the zoom among them (+ and −, the digits, f: a slide's keys,
  // DocumentView.tsx) — reported as it was pressed. The browser's own zoom
  // (Ctrl and + − 0) is held back, as a slide's viewport holds it back: the
  // document zooms, the app does not.
  const NATIVE = new Set(['PageUp', 'PageDown', 'Home', 'End', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' ']);
  const BROWSER_ZOOM = new Set(['+', '=', '-', '_', '0']);
  document.addEventListener('keydown', (e) => {
    if (NATIVE.has(e.key)) return;
    if ((e.ctrlKey || e.metaKey) && BROWSER_ZOOM.has(e.key)) e.preventDefault();
    post({ type: 'key', key: e.key, code: e.code, ctrlKey: e.ctrlKey, metaKey: e.metaKey, shiftKey: e.shiftKey, altKey: e.altKey, repeat: e.repeat });
  });
  // A pinch, or Ctrl and the wheel, zooms the document rather than the page.
  scroll.addEventListener('wheel', (e) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    zoom({ mode: e.deltaY < 0 ? 'in' : 'out' });
  }, { passive: false });

  post({ type: 'hello' });
})();
</script>
</body></html>`;
